import React, { useCallback, useState, useEffect } from "react";
import PaymentMethodImages from "../../common/PaymentMethodImages";
import { marketingUrl } from "../../../utils/Constants";
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    backgroundColor: "var(--white)",
  },
  title: {
    fontFamily: "Segoe UI",
    fontWeight: "bold",
    fontSize: "16px",
    lineHeight: "18px",
    color: "var(--black1)",
    margin: 0,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  sectionTitle: {
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "1.3",
    letterSpacing: "-0.14px",
    color: "var(--black1)",
    margin: 0,
  },
  bankingHeader: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  bankIcon: {
    color: "#467fcf",
    fontSize: "12px",
  },
  bankingTitle: {
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "1.3",
    letterSpacing: "-0.12px",
    color: "var(--black1)",
    margin: 0,
  },
  detailsRow: (isMobile) => ({
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    alignItems: "flex-start",
    gap: isMobile ? "16px" : "8px",
    width: "100%",
  }),
  detailItem: (isMobile) => ({
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: isMobile ? "100%" : "auto",
    marginRight: isMobile ? 0 : undefined,
    flexShrink: isMobile ? 1 : 0,
  }),
  detailLabel: {
    fontFamily: "Segoe UI",
    fontWeight: 300,
    fontSize: "10px",
    lineHeight: "11px",
    color: "var(--gray1)",
    margin: 0,
  },
  detailValue: (isMobile) => ({
    fontFamily: "Segoe UI",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "11px",
    color: "#1d1d1d",
    margin: 0,
    whiteSpace: isMobile ? "normal" : "nowrap",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  }),
  detailValueBold: (isMobile) => ({
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "14px",
    color: "#1d1d1d",
    margin: 0,
    whiteSpace: isMobile ? "normal" : "nowrap",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  }),
  copyWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  copyButton: (isCopied, isHovered) => ({
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    color: isCopied ? "var(--green, #5cb85c)" : (isHovered ? "var(--button-color)" : "var(--blue)"),
    fontSize: "14px",
    lineHeight: "14px",
    display: "flex",
    alignItems: "center",
    transition: "color 0.3s ease, transform 0.2s ease",
    transform: isHovered && !isCopied ? "scale(1.1)" : "scale(1)",
  }),
  warningText: {
    fontFamily: "Segoe UI",
    fontSize: "12px",
    lineHeight: "14px",
    color: "#c1232c",
    margin: 0,
  },
  warningBold: {
    fontWeight: "bold",
  },
  divider: {
    height: "1px",
    backgroundColor: "#d7dee8",
    width: "100%",
    marginTop: "4px",
  },
  creditCardText: {
    fontFamily: "Segoe UI",
    fontSize: "14px",
    lineHeight: "normal",
    color: "var(--lightGray3)",
    margin: 0,
  },
  copiedToast: {
    position: "fixed",
    bottom: "20px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "var(--black1)",
    color: "var(--white)",
    padding: "8px 16px",
    borderRadius: "4px",
    fontFamily: "Segoe UI",
    fontSize: "14px",
    zIndex: 9999,
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
  },
};

const BANKING_DETAILS = {
  accountName: "The Academic Institute of Excellence",
  bank: "ABSA",
  branchCode: "632005",
  accountNumber: "4080 8130 53",
};

function PaymentMethods({ studentNumber = "" }) {
  const [copiedField, setCopiedField] = useState(null);
  const [hoveredField, setHoveredField] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleCopy = useCallback(async (value, fieldName) => {
    try {
      await navigator.clipboard.writeText(value.replace(/\s/g, ""));
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, []);

  const paymentReference = studentNumber || "Your Student Number";

  return (
    <div style={styles.container}>
      <p style={styles.title}>Payment methods</p>

      <div style={styles.section}>

        {/* Credit Card Section */}
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <p style={styles.sectionTitle}>1. Credit Card payments</p>
          <p style={styles.creditCardText}>
            You can easily make payments using your credit or debit card, zapper, or other payment methods once payment details has been confirmed.
          </p>
          <PaymentMethodImages
            imageHeight={'20px'}
            enabled={{
              creditCard: true,
              debitCard: false,
              instantEFT: true,
              scanToPay: true,
              snapscan: false,
              zapper: true,
            }}
          />
        </div>
        {/* Divider */}
        <div style={styles.divider}></div>
        {/* EFT Section */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <p style={styles.sectionTitle}>2. EFT (Electronic Funds Transfer)</p>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>

            <div style={styles.detailsRow(isMobile)}>
              <div style={{
                ...styles.detailItem(isMobile),
                ...(!isMobile && { width: "220px", marginRight: "40px" })
              }}>
                <p style={styles.detailLabel}>Banking Details</p>
                <p style={styles.detailValue(isMobile)}><a href={`${marketingUrl}/payment`} target="_blank">{marketingUrl}</a></p>
              </div>
              <div style={{
                ...styles.detailItem(isMobile),
                ...(!isMobile && { flex: 1 })
              }}>
                <p style={styles.detailLabel}>Payment Reference</p>
                <div style={styles.copyWrapper}>
                  <p style={styles.detailValueBold(isMobile)}>{paymentReference}</p>
                  {studentNumber && (
                    <button
                      type="button"
                      style={styles.copyButton(copiedField === "paymentReference", hoveredField === "paymentReference")}
                      onClick={() => handleCopy(studentNumber, "paymentReference")}
                      onMouseEnter={() => setHoveredField("paymentReference")}
                      onMouseLeave={() => setHoveredField(null)}
                      title="Copy payment reference"
                    >
                      <i className={copiedField === "paymentReference" ? "fal fa-check-circle" : "fal fa-copy"}></i>
                    </button>
                  )}
                </div>
              </div>
            </div>

            <p style={styles.warningText}>
              Your <span style={styles.warningBold}>student number</span> must appear as the reference on your EFT form or deposit slip (e.g., '{studentNumber || "12345"}')
            </p>
          </div>
        </div>

      </div>

      {/* Copied Toast */}
      {copiedField && (
        <div style={styles.copiedToast}>
          Copied to clipboard!
        </div>
      )}
    </div>
  );
}

export default PaymentMethods;

